import React from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';

import { Card, createStyles, Grid, Theme, withStyles, WithStyles } from '@material-ui/core';
import classnames from 'classnames';
import { get } from 'lodash';
import moment from 'moment';
import { Appointment } from 'requests/resources/appointment';

import { Availability } from '@lib/resources/types';

import AppointmentStatusInfo from './AppointmentStatusInfo';
import CompactAppointmentDetails from './CompactAppointmentDetails';

const messages = defineMessages({
  confirmedActionText: {
    id: 'components.v2.CardAppointmentStatus.confirmedActionText',
    defaultMessage: 'Need to make changes?',
  },
  canceledActionText: {
    id: 'components.v2.CardAppointmentStatus.canceledActionText',
    defaultMessage: 'Changed your mind?',
  },
});

const styles = ({ spacing: { unit }, custom, palette }: Theme) =>
  createStyles({
    baseCard: {
      boxShadow: '0px 4px 16px rgba(125, 125, 125, 0.25)',
    },
    success: {
      borderTop: `6px solid ${custom.palette.colorAccentAloe}`,
    },
    canceled: {
      borderTop: `6px solid ${custom.palette.colorDanger}`,
    },
    unconfirmed: {
      borderTop: `6px solid ${custom.palette.colorSupportSteel}`,
    },
    pending: {
      borderTop: `6px solid ${palette.secondary.main}`,
    },
    padding: {
      padding: `0 10% ${unit * 4}px`,
    },
    // todo: remove once canceled supports actions bar
    extraBottomPadding: {
      paddingBottom: unit * 3,
    },
  });

enum SUPPORTED_STATUS {
  Confirmed = 'confirmed',
  Cancelled = 'cancelled',
  BookedPending = 'booked-pending',
  Unconfirmed = 'unconfirmed',
  Available = 'available',
}

export interface CardAppointmentStatusProps extends WithStyles<typeof styles> {
  appointment: Appointment | Availability;
  ActionsBar?: React.ReactNode;
  compact?: boolean;
  lockTimeout?: number;
}

const CardAppointmentStatus: React.FC<CardAppointmentStatusProps> = ({
  appointment,
  classes,
  ActionsBar,
  compact,
  lockTimeout,
}) => {
  const { formatMessage } = useIntl();

  function getStatusObject(status: string) {
    switch (status) {
      case SUPPORTED_STATUS.Confirmed:
        return {
          text: formatMessage(messages.confirmedActionText),
          style: classes.success,
        };
      case SUPPORTED_STATUS.Cancelled:
        return {
          text: formatMessage(messages.canceledActionText),
          style: classes.canceled,
        };
      case SUPPORTED_STATUS.BookedPending:
        return {
          style: classes.pending,
        };
      case SUPPORTED_STATUS.Unconfirmed:
      case SUPPORTED_STATUS.Available:
      default:
        return { style: classes.unconfirmed };
    }
  }

  if (!appointment.status) return null;
  const validStatus = Object.values(SUPPORTED_STATUS);
  if (!validStatus.includes(appointment.status as SUPPORTED_STATUS)) return null;

  const statusObject = getStatusObject(get(appointment, 'status'));
  const isPastAppointment = moment().diff(appointment.date) >= 0;

  return (
    <Grid
      container
      item
      component={Card}
      // @ts-ignore
      elevation={0}
      xs={12}
      className={classnames(classes.baseCard, statusObject.style, {
        [classes.extraBottomPadding]: isPastAppointment,
      })}
    >
      <AppointmentStatusInfo appointmentStatus={appointment.status} compact={compact} lockTimeout={lockTimeout} />
      {!isPastAppointment && (
        <Grid item container direction="column" alignContent="center" xs={12} className={classes.padding}>
          {ActionsBar}
          {compact && <CompactAppointmentDetails appointment={appointment} />}
        </Grid>
      )}
    </Grid>
  );
};

export default withStyles(styles)(CardAppointmentStatus);
